/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
    DataLayerEventName,
    PagePosition,
    PlayerEvent,
} from '@news-mono/web-common'
import { useScript } from '@west-australian-newspapers/use-script'
import { getImaOptions } from '../../content/Video/utils/get-ima-options'
import { UnmuteButton } from '../../content/Video/components/UnmuteButton/UnmuteButton'
import { AdEventType, createAdEventEmitter } from './utils/ad-event-helpers'
import {
    DEFAULT_AD_REQUEST_TIMEOUT_MS,
    useAdFailSafeTimeouts,
} from './utils/useAdFailSafeTimeouts'
import { useAdProgress } from './utils/useAdProgress'
import {
    StyledAdContainer,
    StyledAdLoader,
    StyledAdMuteWrapper,
    StyledAdOverlay,
    StyledAdPauseButton,
    StyledAdSlotRoot,
    StyledAdTimelineHitArea,
    StyledAdTimelineProgress,
    StyledAdTimelineTrack,
    StyledAdVideo,
} from './VerticalVideoAdSlot.styled'

interface VerticalVideoAdSlotProps {
    adUnitPath: string
    isMuted: boolean
    onMuteToggle: () => void
    onAdComplete?: () => void
    onAdError?: () => void
    onSkippableChange?: (isSkippable: boolean) => void
    isActive: boolean
    videoId: string
    publicationPathname: string
    position: number
    onEvent?: (event: PlayerEvent) => void
    pagePosition?: PagePosition
}

export const VerticalVideoAdSlot: React.FC<VerticalVideoAdSlotProps> = ({
    adUnitPath,
    isMuted,
    onMuteToggle,
    onAdComplete,
    onAdError,
    onSkippableChange,
    isActive,
    videoId,
    publicationPathname,
    position,
    onEvent,
    pagePosition = 'inline-article',
}) => {
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const adContainerRef = useRef<HTMLDivElement | null>(null)
    const adsManagerRef = useRef<any>(null)
    const adsLoaderRef = useRef<any>(null)
    const requestAdsRef = useRef<(() => void) | null>(null)
    const finishAdRef = useRef<(() => void) | null>(null)
    const emitAdEventRef = useRef<ReturnType<
        typeof createAdEventEmitter
    > | null>(null)
    const isMutedRef = useRef(isMuted)
    const isPausedRef = useRef(false)
    const milestoneRef = useRef({
        m25: false,
        m50: false,
        m75: false,
    })
    const pauseNextAdRef = useRef(false)
    const autoPausedAdRef = useRef(false)
    const hasAdStartedRef = useRef(false)
    const didCompleteRef = useRef(false)
    const isActiveRef = useRef(false)
    const durationRef = useRef<number | null>(null)
    const [isPaused, setIsPaused] = useState(false)
    const [isAdActive, setIsAdActive] = useState(false)
    const {
        progress,
        elapsedSeconds,
        setElapsedSeconds,
        startProgressTimer,
        clearProgressTimer,
        resetProgress,
    } = useAdProgress({
        getRemainingTime: () => {
            const adsManager = adsManagerRef.current
            const remaining = adsManager?.getRemainingTime?.()
            return typeof remaining === 'number' ? remaining : null
        },
        getDuration: () => durationRef.current,
    })
    const [isAdLoading, setIsAdLoading] = useState(false)
    const [isSkippable, setIsSkippable] = useState(false)
    const [isSkippableAd, setIsSkippableAd] = useState(false)
    const [skipOffsetSeconds, setSkipOffsetSeconds] = useState<number | null>(
        null,
    )

    useEffect(() => {
        isMutedRef.current = isMuted
    }, [isMuted])

    useEffect(() => {
        isPausedRef.current = isPaused
    }, [isPaused])

    useEffect(() => {
        if (!isAdActive) return
        if (!emitAdEventRef.current) return
        emitAdEventRef.current(DataLayerEventName.adVolumeChange as AdEventType)
    }, [isAdActive, isMuted])

    useEffect(() => {
        if (!isAdActive) return
        if (!emitAdEventRef.current) return
        if (!milestoneRef.current.m25 && progress >= 0.25) {
            milestoneRef.current.m25 = true
            emitAdEventRef.current(DataLayerEventName.adMilestone25)
        }
        if (!milestoneRef.current.m50 && progress >= 0.5) {
            milestoneRef.current.m50 = true
            emitAdEventRef.current(DataLayerEventName.adMilestone50)
        }
        if (!milestoneRef.current.m75 && progress >= 0.75) {
            milestoneRef.current.m75 = true
            emitAdEventRef.current(DataLayerEventName.adMilestone75)
        }
    }, [isAdActive, progress])

    const { loading, error } = useScript({
        src: 'https://imasdk.googleapis.com/js/sdkloader/ima3.js',
    })

    useEffect(() => {
        isActiveRef.current = isActive
    }, [isActive])

    const handleInitFailure = useCallback(() => {
        if (!isActiveRef.current) return
        onAdError?.()
    }, [onAdError])

    const getIsImaAvailable = useCallback(
        () => Boolean((window as any).google?.ima),
        [],
    )

    const { startRequestTimeout, clearRequestTimeout, clearAllTimeouts } =
        useAdFailSafeTimeouts({
            isActive,
            loading,
            error,
            onInitFailure: handleInitFailure,
            getIsImaAvailable,
        })

    useEffect(() => {
        if (!isActive) return
        const adsManager = adsManagerRef.current
        if (!adsManager) return
        if (autoPausedAdRef.current) {
            // Resume ad if it was auto-paused during preload.
            autoPausedAdRef.current = false
            adsManager.resume?.()
        }
    }, [isActive])

    useEffect(() => {
        if (!isActive) return
        if (loading || error) return
        if (!videoRef.current || !adContainerRef.current) return
        const ima = (window as any).google?.ima
        if (!ima) return

        const adDisplayContainer = new ima.AdDisplayContainer(
            adContainerRef.current,
            videoRef.current,
        )
        const adsLoader = new ima.AdsLoader(adDisplayContainer)
        adsLoaderRef.current = adsLoader

        const emitAdEvent = createAdEventEmitter({
            onEvent,
            position,
            pagePosition,
            getMuted: () => isMutedRef.current,
            getPaused: () => isPausedRef.current,
            getSize: () => ({
                width: adContainerRef.current?.offsetWidth ?? 0,
                height: adContainerRef.current?.offsetHeight ?? 0,
            }),
        })
        emitAdEventRef.current = emitAdEvent

        const onAdsManagerLoaded = (event: any) => {
            const adsManager = event.getAdsManager(videoRef.current)
            adsManagerRef.current = adsManager
            emitAdEvent(DataLayerEventName.adLoad)

            adsManager.addEventListener(
                ima.AdErrorEvent.Type.AD_ERROR,
                (event: any) => {
                    const error =
                        typeof event?.getError === 'function'
                            ? event.getError()
                            : null
                    const errorType = error?.getErrorCode
                        ? String(error.getErrorCode())
                        : 'ad-error'
                    handleAdError(errorType)
                },
            )

            adsManager.addEventListener(ima.AdEvent.Type.CLICK, () => {
                emitAdEvent(DataLayerEventName.adClick)
            })

            adsManager.addEventListener(ima.AdEvent.Type.STARTED, () => {
                clearRequestTimeout()
                emitAdEvent(DataLayerEventName.adStart)
                emitAdEvent(DataLayerEventName.adPlay)
                milestoneRef.current = { m25: false, m50: false, m75: false }
                hasAdStartedRef.current = true
                didCompleteRef.current = false
                const currentAd = adsManager.getCurrentAd?.()
                const duration =
                    typeof currentAd?.getDuration === 'function'
                        ? currentAd.getDuration()
                        : null
                const skipTimeOffset =
                    typeof currentAd?.getSkipTimeOffset === 'function'
                        ? currentAd.getSkipTimeOffset()
                        : -1
                const supportsSkip =
                    typeof skipTimeOffset === 'number'
                        ? skipTimeOffset >= 0
                        : false
                const nextSkippable =
                    typeof currentAd?.isSkippable === 'function'
                        ? currentAd.isSkippable()
                        : false
                setIsSkippableAd(supportsSkip)
                setSkipOffsetSeconds(supportsSkip ? skipTimeOffset : null)
                setIsSkippable(nextSkippable)
                durationRef.current = duration
                setIsAdLoading(false)
                setIsAdActive(true)
                setElapsedSeconds(0)
                if (pauseNextAdRef.current) {
                    pauseNextAdRef.current = false
                    autoPausedAdRef.current = true
                    adsManager.pause?.()
                    setIsPaused(true)
                    resetProgress()
                    return
                } else {
                    autoPausedAdRef.current = false
                    setIsPaused(false)
                }
                startProgressTimer()
            })

            adsManager.addEventListener(ima.AdEvent.Type.PAUSED, () => {
                setIsPaused(true)
                emitAdEvent(DataLayerEventName.adPause)
                clearProgressTimer()
            })

            adsManager.addEventListener(ima.AdEvent.Type.RESUMED, () => {
                setIsPaused(false)
                autoPausedAdRef.current = false
                emitAdEvent(DataLayerEventName.adPlay)
                startProgressTimer()
            })

            adsManager.addEventListener(
                ima.AdEvent.Type.SKIPPABLE_STATE_CHANGED,
                () => {
                    const currentAd = adsManager.getCurrentAd?.()
                    const nextSkippable =
                        typeof currentAd?.isSkippable === 'function'
                            ? currentAd.isSkippable()
                            : false
                    setIsSkippable(nextSkippable)
                },
            )

            const finishAd = () => {
                if (didCompleteRef.current) {
                    return
                }
                clearRequestTimeout()
                emitAdEvent(DataLayerEventName.adEnd)
                didCompleteRef.current = true
                setIsAdActive(false)
                setIsPaused(false)
                setIsAdLoading(false)
                resetProgress()
                setIsSkippableAd(false)
                setSkipOffsetSeconds(null)
                setIsSkippable(false)
                durationRef.current = null
                milestoneRef.current = { m25: false, m50: false, m75: false }

                onAdComplete?.()

                if (requestAdsRef.current && hasAdStartedRef.current) {
                    pauseNextAdRef.current = true
                    requestAdsRef.current()
                }

                autoPausedAdRef.current = false
                hasAdStartedRef.current = false
            }

            finishAdRef.current = finishAd

            adsManager.addEventListener(ima.AdEvent.Type.SKIPPED, finishAd)
            adsManager.addEventListener(ima.AdEvent.Type.COMPLETE, finishAd)
            adsManager.addEventListener(
                ima.AdEvent.Type.ALL_ADS_COMPLETED,
                () => {
                    emitAdEvent(DataLayerEventName.adPodComplete)
                    finishAd()
                },
            )

            adsManager.init(
                adContainerRef.current?.offsetWidth || 0,
                adContainerRef.current?.offsetHeight || 0,
                ima.ViewMode.NORMAL,
            )
            adsManager.start()
            adsManager.setVolume(isMuted ? 0 : 1)
        }

        const handleAdError = (errorType = 'ad-error') => {
            clearRequestTimeout()
            emitAdEvent(DataLayerEventName.adError, {
                errorType,
            })
            adsManagerRef.current?.destroy?.()
            setIsAdActive(false)
            setIsPaused(false)
            setIsAdLoading(false)
            resetProgress()
            setIsSkippableAd(false)
            setSkipOffsetSeconds(null)
            setIsSkippable(false)
            durationRef.current = null
            milestoneRef.current = { m25: false, m50: false, m75: false }
            onAdError?.()
        }

        const onAdsLoaderError = (event: any) => {
            const error =
                typeof event?.getError === 'function' ? event.getError() : null
            const errorType = error?.getErrorCode
                ? String(error.getErrorCode())
                : 'ad-error'
            handleAdError(errorType)
        }

        adsLoader.addEventListener(
            ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
            onAdsManagerLoaded,
        )
        adsLoader.addEventListener(
            ima.AdErrorEvent.Type.AD_ERROR,
            onAdsLoaderError,
        )

        const { serverUrl } = getImaOptions({
            adUnitPath,
            videoId,
            publicationPathname,
        })

        const requestAds = () => {
            if (!adContainerRef.current) return
            const adsRequest = new ima.AdsRequest()
            clearRequestTimeout()
            setIsAdLoading(true)
            didCompleteRef.current = false
            setElapsedSeconds(0)
            setIsSkippableAd(false)
            setSkipOffsetSeconds(null)
            setIsSkippable(false)
            milestoneRef.current = { m25: false, m50: false, m75: false }
            emitAdEvent(DataLayerEventName.adRequest)
            adsRequest.adTagUrl = serverUrl
            adsRequest.linearAdSlotWidth = adContainerRef.current.offsetWidth
            adsRequest.linearAdSlotHeight = adContainerRef.current.offsetHeight
            adsRequest.nonLinearAdSlotWidth = adContainerRef.current.offsetWidth
            adsRequest.nonLinearAdSlotHeight = Math.floor(
                adContainerRef.current.offsetHeight / 3,
            )

            try {
                // Note: IMA may require a user gesture on mobile before initialize/start succeeds.
                adDisplayContainer.initialize()
                adsLoader.requestAds(adsRequest)
                startRequestTimeout(() => {
                    if (!hasAdStartedRef.current && !didCompleteRef.current) {
                        handleAdError('ad-request-timeout')
                    }
                }, DEFAULT_AD_REQUEST_TIMEOUT_MS)
            } catch {
                handleAdError('ad-request-error')
            }
        }

        requestAdsRef.current = requestAds

        requestAds()

        return () => {
            finishAdRef.current = null
            emitAdEventRef.current = null
            adsLoader.removeEventListener(
                ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
                onAdsManagerLoaded,
            )
            adsLoader.removeEventListener(
                ima.AdErrorEvent.Type.AD_ERROR,
                onAdsLoaderError,
            )
            adsManagerRef.current?.destroy?.()
            adsLoaderRef.current = null
            requestAdsRef.current = null
            clearAllTimeouts()
            clearProgressTimer()
        }
    }, [
        adUnitPath,
        clearAllTimeouts,
        clearRequestTimeout,
        error,
        isActive,
        loading,
        publicationPathname,
        startRequestTimeout,
        videoId,
    ])

    useEffect(() => {
        if (!adsManagerRef.current) return
        adsManagerRef.current.setVolume(isMuted ? 0 : 1)
    }, [isMuted])

    const handleTogglePause = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault()
        event.stopPropagation()
        const adsManager = adsManagerRef.current
        if (!adsManager) return
        if (isPaused) {
            adsManager.resume?.()
        } else {
            adsManager.pause?.()
        }
    }

    const hasSkipOffset = skipOffsetSeconds !== null
    const skipDelaySeconds = skipOffsetSeconds ?? 0
    const canSkipByTime =
        skipOffsetSeconds !== null && elapsedSeconds >= skipDelaySeconds
    const canSkipNow =
        isSkippableAd && hasSkipOffset && canSkipByTime && isSkippable

    useEffect(() => {
        if (!onSkippableChange) return
        onSkippableChange(isAdActive && canSkipNow)
    }, [canSkipNow, isAdActive, onSkippableChange])

    return (
        <StyledAdSlotRoot>
            <StyledAdContainer ref={adContainerRef} />
            <StyledAdVideo ref={videoRef} muted={isMuted} playsInline />
            <StyledAdMuteWrapper>
                <UnmuteButton
                    variant="vertical"
                    positionState={{ alignToTop: true }}
                    isMuted={isMuted}
                    action={onMuteToggle}
                />
            </StyledAdMuteWrapper>
            {isAdLoading && (
                <StyledAdLoader className="vjs-waiting">
                    <div className="vjs-loading-spinner" />
                </StyledAdLoader>
            )}
            {isAdActive && (
                <StyledAdOverlay>
                    <StyledAdPauseButton
                        type="button"
                        onClick={handleTogglePause}
                        aria-label={isPaused ? 'Play ad' : 'Pause ad'}
                    >
                        {isPaused ? '▶' : '❚❚'}
                    </StyledAdPauseButton>
                    <StyledAdTimelineHitArea>
                        <StyledAdTimelineTrack className="ad-timeline-track">
                            <StyledAdTimelineProgress progress={progress} />
                        </StyledAdTimelineTrack>
                    </StyledAdTimelineHitArea>
                </StyledAdOverlay>
            )}
        </StyledAdSlotRoot>
    )
}
